import { upsertPrompt } from 'actions';
import Modal from 'components/Modal';
import SelectRedesign from 'components/Select/SelectRedesign';
import { deploymentOptions, kindOptions, sourceOptions } from 'constants/entities';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Prompt } from 'types';

type ComponentProps = {
  isModalOpen: boolean;
  toggleModalOpen: () => void;
  prompt?: Prompt;
  isCreating?: boolean;
  redirect?: boolean;
};

const EditPromptModal: React.FC<ComponentProps> = ({
  isModalOpen,
  toggleModalOpen,
  prompt,
  isCreating,
  redirect = false,
}) => {
  const dispatch = useDispatch();

  const [kind, setKind] = useState(prompt?.kind || '');
  const [deployment, setDeployment] = useState(prompt?.deployment || '');
  const [source, setSource] = useState(prompt?.source || '');
  const [errors, setErrors] = useState<Record<string, string>>();

  const { register, getValues, handleSubmit } = useForm<{
    kind: string;
    deployment: string;
    content: string;
    source: string;
    name: string;
    description: string;
  }>({
    defaultValues: {
      kind: prompt?.kind || '',
      deployment: prompt?.deployment || '',
      content: prompt?.content || '',
      source: prompt?.source ?? '',
      // @ts-ignore
      name: prompt?.name || '',
      // @ts-ignore
      description: prompt?.description || '',
    },
  });

  const onSubmit = (): void => {
    const { content, name, description } = getValues();

    if (!name) {
      setErrors({ name: 'You must provide a name' });
      return;
    }
    if (!kind) {
      setErrors({ kind: 'You must select a type' });
      return;
    }
    if (!deployment) {
      setErrors({ deployment: 'You must select a deployment' });
      return;
    }
    if (!source) {
      setErrors({ source: 'You must select a source' });
      return;
    }
    if (!content) {
      setErrors({ prompt: 'You must provide a prompt' });
      return;
    }

    setErrors({});

    const newPrompt = { ...prompt };

    if (isCreating) {
      delete newPrompt.uuid;
    }

    dispatch(
      upsertPrompt({
        ...newPrompt,
        kind,
        deployment,
        content,
        source,
        name,
        description,
        redirect,
        ...(isCreating ? { relationships: [] } : {}),
      })
    );
    toggleModalOpen();
  };

  const handleSaveButton = (): void => {
    handleSubmit(onSubmit)();
  };

  const renderModelForm = (
    <div className="pt-1 pb-6 flex flex-col gap-4">
      <span className="text-heading-1">{isCreating ? 'New prompt' : 'Edit prompt'}</span>
      <div className="">
        <form className="flex flex-col items-start">
          <div className="flex flex-col items-start gap-2 w-full">
            <div className="flex flex-row gap-4">
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-1">
                  <span>Name</span>
                  <span className="litlingo-red-color">*</span>
                </div>
                <input
                  id="name"
                  ref={register({ required: true })}
                  name="name"
                  placeholder="Prompt name"
                  className="form-input w-88 p-2 text-body rounded placeholder-italic placeholder-litlingo-gray-6"
                />
                <div className="text-small">
                  <span className="text-litlingo-alert">{errors?.name}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-1">
                  <span>Type</span>
                  <span className="litlingo-red-color">*</span>
                </div>
                <div className="flex-shrink-0 w-44">
                  <SelectRedesign
                    onChange={(value: string): void => setKind(value)}
                    options={kindOptions}
                    value={kind}
                    placeholder="Type"
                    dataTestid="review-type-select"
                    className="h-9"
                  />
                  <div className="text-small">
                    <span className="text-litlingo-alert">{errors?.kind}</span>
                  </div>
                </div>
              </div>
            </div>

            <label htmlFor="description" className="w-full flex flex-col gap-1">
              <div className="flex flex-row gap-1">
                <span>Description</span>
              </div>
              <textarea
                id="description"
                ref={register()}
                name="description"
                placeholder="A short description that can help others understand the purpose of this prompt."
                rows={2}
                className="form-input w-136 p-2 text-body rounded resize-none placeholder-italic placeholder-litlingo-gray-6"
              />
            </label>

            <div className="flex flex-row gap-4">
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-1">
                  <span>Deploy with</span>
                  <span className="litlingo-red-color">*</span>
                </div>
                <div className="flex-shrink-0 w-50">
                  <SelectRedesign
                    onChange={(value: string): void => setDeployment(value)}
                    options={deploymentOptions}
                    value={deployment}
                    placeholder="Select deployment"
                    dataTestid="review-type-select"
                    className="h-9"
                  />
                  <div className="text-small">
                    <span className="text-litlingo-alert">{errors?.deployment}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-1">
                  <span>Source</span>
                  <span className="litlingo-red-color">*</span>
                </div>
                <div className="flex-shrink-0 w-50">
                  <SelectRedesign
                    onChange={(value: string): void => setSource(value)}
                    options={sourceOptions}
                    value={source}
                    placeholder="Select source"
                    dataTestid="review-type-select"
                    className="h-9"
                  />
                  <div className="text-small">
                    <span className="text-litlingo-alert">{errors?.source}</span>
                  </div>
                </div>
              </div>
            </div>

            <label htmlFor="content" className="flex flex-col gap-1 mt-4">
              <div className="flex flex-row gap-1">
                <span>Prompt</span>
                <span className="litlingo-red-color">*</span>
              </div>
              <textarea
                id="content"
                ref={register()}
                name="content"
                rows={17}
                placeholder="Add your prompt here. For the best results make sure to provide detailed instructions, be specific as possible and provide context."
                className="form-input w-136 p-2 text-body rounded resize-none placeholder-italic placeholder-litlingo-gray-6"
              />
              <div className="text-small">
                <span className="text-litlingo-alert">{errors?.prompt}</span>
              </div>
            </label>
            <span className="text-small">{`Content: {input} Format instructions {format_instructions}`}</span>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <>
      {isModalOpen && (
        <Modal
          body={renderModelForm}
          title=" "
          okButton
          okButtonText="Save"
          okButtonOnClick={handleSaveButton}
          toggleShowModal={toggleModalOpen}
        />
      )}
    </>
  );
};

export default EditPromptModal;
